'use client';

import { c as _c } from "react/compiler-runtime";
import { useUAContext } from '@/context/UAContext';
import { enterFullscreen } from '@/lib/fullscreen';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEventListener, useMediaQuery } from 'usehooks-ts';
type UseAutoFullscreenProps = {
  targetId: string;
  enableModal: boolean;
  /**
   * When false, leaving fullscreen stops further automatic attempts. Use it where the viewer can
   * leave fullscreen and stay on the page: leaving changes the orientation back to landscape,
   * and the click that left would otherwise let the automatic attempt succeed right away.
   */
  retryAfterExit?: boolean;
};
export function useAutoFullscreen(t0) {
  const $ = _c(18);
  const {
    targetId,
    enableModal,
    retryAfterExit: t1
  } = t0;
  const retryAfterExit = t1 === undefined ? true : t1;
  const {
    device
  } = useUAContext();
  const [showModal, setShowModal] = useState(false);
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const isMobileBrowser = device === "mobile_browser";
  const wasFullscreenRef = useRef(false);
  const hasExitedFullscreenRef = useRef(false);
  const documentRef = useRef(typeof document !== "undefined" ? document : null);
  let t2;
  if ($[0] !== retryAfterExit) {
    t2 = () => {
      if (retryAfterExit) {
        return;
      }
      if (document.fullscreenElement) {
        wasFullscreenRef.current = true;
      } else {
        if (wasFullscreenRef.current) {
          hasExitedFullscreenRef.current = true;
        }
      }
    };
    $[0] = retryAfterExit;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  useEventListener("fullscreenchange", t2, documentRef);
  let t3;
  if ($[2] !== enableModal || $[3] !== targetId) {
    t3 = async () => {
      if (document.fullscreenElement) {
        return;
      }
      try {
        await enterFullscreen(targetId);
      } catch {
        if (enableModal) {
          setShowModal(true);
        }
      }
    };
    $[2] = enableModal;
    $[3] = targetId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const tryFullscreen = t3;
  let t4;
  if ($[5] !== targetId) {
    t4 = async () => {
      setShowModal(false);
      ;
      try {
        await enterFullscreen(targetId);
      } catch (t5) {
        const error = t5;
        console.error("[useAutoFullscreen] onConfirm", error);
      }
    };
    $[5] = targetId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onConfirm = t4;
  let t5;
  let t6;
  if ($[7] !== isLandscape || $[8] !== isMobileBrowser || $[9] !== retryAfterExit || $[10] !== targetId || $[11] !== tryFullscreen) {
    t5 = () => {
      const target = document.getElementById(targetId);
      if (!isMobileBrowser || typeof target?.requestFullscreen !== "function") {
        return;
      }
      if (document.fullscreenElement) {
        wasFullscreenRef.current = true;
      }
      if (isLandscape) {
        if (!retryAfterExit) {
          if (wasFullscreenRef.current && !document.fullscreenElement) {
            hasExitedFullscreenRef.current = true;
          }
          if (hasExitedFullscreenRef.current) {
            return;
          }
        }
        tryFullscreen();
      } else {
        setShowModal(false);
      }
    };
    t6 = [isLandscape, isMobileBrowser, retryAfterExit, targetId, tryFullscreen];
    $[7] = isLandscape;
    $[8] = isMobileBrowser;
    $[9] = retryAfterExit;
    $[10] = targetId;
    $[11] = tryFullscreen;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  if (!isMobileBrowser) {
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        showModal: false,
        onConfirm: _temp
      };
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    return t7;
  }
  let t7;
  if ($[15] !== onConfirm || $[16] !== showModal) {
    t7 = {
      showModal,
      onConfirm
    };
    $[15] = onConfirm;
    $[16] = showModal;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp() {}